import {isNilOrEmpty, isObject, isString} from 'ramda-adjunct';

import {GetFileByIdApiResponse} from '@dms/api/base';
import {
  ColorV2ResponseBody,
  ConditionEnum,
  GetVehicleApiResponse,
  VehicleTypeEnum,
} from '@dms/api/vehicle';

import {isNotNilOrEmpty, parseDate} from 'shared/web';

import {MAKE_OTHER, MODEL_FAMILY_OTHER_END} from '../constants/makeModel';
import {BaseVehicle} from '../types/BaseVehicle';
import {getCustomFieldValue} from '../utils/getCustomFieldValue';
import {getUmweltplaketteValue} from '../utils/getUmweltplaketteValue';

const toColorValue = (
  color: ColorV2ResponseBody | string | null | undefined
): string | undefined => {
  if (isString(color)) {
    return color;
  }
  if (isObject(color) && color.value) {
    return color.value;
  }
  return undefined;
};

const toArrayOrEmpty = <T>(value: T | null | undefined): T[] =>
  isNotNilOrEmpty(value) ? [value] : [];

export const toBaseVehicle = (
  apiVehicle: GetVehicleApiResponse,
  certificateFile?: GetFileByIdApiResponse
): BaseVehicle | undefined => {
  if (isNilOrEmpty(apiVehicle)) {
    return undefined;
  }
  const manufacturedOnDate = parseDate(apiVehicle.manufacturedOn);
  const hasMonth = apiVehicle.manufacturedOn && apiVehicle.manufacturedOn.includes('-');

  return {
    ...apiVehicle,

    // UI has both form display and actual values for make/model
    formMake: (apiVehicle.make === MAKE_OTHER ? apiVehicle.customMake : apiVehicle.make) ?? '',
    formModelFamily:
      (apiVehicle.modelFamily?.endsWith(MODEL_FAMILY_OTHER_END)
        ? apiVehicle.customModelFamily
        : apiVehicle.modelFamily) ?? '',

    exteriorColor: toColorValue(apiVehicle.exteriorColor),
    interiorColor: toColorValue(apiVehicle.interiorColor),

    // German-specific fields from customFields using utility functions
    hsn: getCustomFieldValue(apiVehicle.customFields, 'manufacturerKeyNumber'),
    tsn: getCustomFieldValue(apiVehicle.customFields, 'typeCodeNumber'),
    umweltplakette: getUmweltplaketteValue(apiVehicle.customFields),

    // UI splits manufactured date into separate year/month fields
    manufacturedOnYear: manufacturedOnDate?.getFullYear().toString(),
    manufacturedOnMonth:
      manufacturedOnDate && hasMonth ? (manufacturedOnDate.getMonth() + 1).toString() : undefined,

    // UI uses arrays for  chips fields (API uses single values)
    type: toArrayOrEmpty(apiVehicle.type) as VehicleTypeEnum[],
    bodyStyle: toArrayOrEmpty(apiVehicle.bodyStyle),
    fuelType: toArrayOrEmpty(apiVehicle.fuelType),
    secondaryFuelType: toArrayOrEmpty(apiVehicle.secondaryFuelType),
    hybridType: toArrayOrEmpty(apiVehicle.hybridType),
    transmission: toArrayOrEmpty(apiVehicle.transmission),
    drive: toArrayOrEmpty(apiVehicle.drive),
    exteriorColorSpecification: toArrayOrEmpty(apiVehicle.exteriorColorSpecification),
    serviceBookType: toArrayOrEmpty(apiVehicle.serviceBookType),
    serviceBookState: toArrayOrEmpty(apiVehicle.serviceBookState),
    doorCountCategory: toArrayOrEmpty(apiVehicle.doorCountCategory),
    seatCount: toArrayOrEmpty(apiVehicle.seatCount?.toString()),
    interiorMaterial: toArrayOrEmpty(apiVehicle.interiorMaterial),

    condition: toArrayOrEmpty(apiVehicle.condition) as ConditionEnum[],

    axleCount: toArrayOrEmpty(apiVehicle.axleCount?.toString()),

    // CO2 emission class fields
    carbonDioxideEmissionClass: apiVehicle.carbonDioxideEmissionClass,
    carbonDioxideEmissionClassDischarged: apiVehicle.carbonDioxideEmissionClassDischarged,
    carbonDioxideEmissionClassWeightedCombined:
      apiVehicle.carbonDioxideEmissionClassWeightedCombined,

    // UI uses simple string array for features (API uses complex objects)
    features: apiVehicle.features?.map((feature) => feature.key) ?? [],

    // Battery certificate document mapping
    batteryCertificateDocument:
      apiVehicle.certificateFileId && certificateFile
        ? {
            fileId: apiVehicle.certificateFileId,
            fileName: certificateFile.originalFilename,
            fileUri: certificateFile.originalUri,
            fileType: certificateFile.contentType,
          }
        : null,
    // UI-specific state management - populate with custom values if they exist
    unknownMakes:
      apiVehicle.customMake && apiVehicle.make === MAKE_OTHER
        ? [{value: apiVehicle.customMake, label: apiVehicle.customMake}]
        : [],
    unknownModels:
      apiVehicle.customModelFamily && apiVehicle.modelFamily?.endsWith(MODEL_FAMILY_OTHER_END)
        ? [{value: apiVehicle.customModelFamily, label: apiVehicle.customModelFamily}]
        : [],
  };
};
